import React from 'react';
// Drop 2 (C-06): numbers-only crisis strip on the error screen (placement
// signed by Alex 5 Oct 2026; no new wording).
import PublicCrisisStrip from './safety/PublicCrisisStrip';

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      retryCount: 0,
      isRecovering: false
    };
  }

  static getDerivedStateFromError(error) {
    // Update state so the next render will show the fallback UI
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    // Log the error details
    console.error('🔥 ErrorBoundary caught an error:', error, errorInfo);
    
    this.setState({
      error: error,
      errorInfo: errorInfo
    });
  }

  handleRetry = () => {
    this.setState({
      isRecovering: true,
      retryCount: this.state.retryCount + 1
    });

    // Wait a bit before clearing the error state
    setTimeout(() => {
      this.setState({
        hasError: false,
        error: null,
        errorInfo: null,
        isRecovering: false
      });
    }, 1000);
  };

  handleRefresh = () => {
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      const { error, retryCount, isRecovering } = this.state;
      
      // Determine error type and appropriate message
      let errorType = 'Unknown Error';
      let errorMessage = 'Something unexpected happened.';
      let actionMessage = 'Please try again.';
      
      if (error) {
        const errorString = error.toString().toLowerCase();
        
        if (errorString.includes('network') || errorString.includes('cors')) {
          errorType = 'Connection Error';
          errorMessage = 'Unable to connect to the AI service.';
          actionMessage = 'Please check your internet connection and try again.';
        } else if (errorString.includes('auth') || errorString.includes('401')) {
          errorType = 'Authentication Error';
          errorMessage = 'Your session has expired.';
          actionMessage = 'Please refresh the page to log in again.';
        } else if (errorString.includes('timeout')) {
          errorType = 'Timeout Error';
          errorMessage = 'The request took too long to complete.';
          actionMessage = 'The AI service may be busy. Please try again in a moment.';
        } else if (errorString.includes('500') || errorString.includes('502') || errorString.includes('504')) {
          errorType = 'Service Error';
          errorMessage = 'The AI service is temporarily unavailable.';
          actionMessage = 'Please wait a moment and try again.';
        }
      }

      return (
        <div className="error-boundary">
          <div className="error-container">
            <div className="error-icon">
              {errorType.includes('Connection') ? '🌐' : 
               errorType.includes('Authentication') ? '🔒' :
               errorType.includes('Timeout') ? '⏱️' :
               errorType.includes('Service') ? '🔧' : '⚠️'}
            </div>
            
            <h2 className="error-title">{errorType}</h2>
            <p className="error-message">{errorMessage}</p>
            <p className="error-action">{actionMessage}</p>
            
            {retryCount > 0 && retryCount < 3 && (
              <p className="retry-info">
                Attempt {retryCount + 1} of 3
              </p>
            )}
            
            <div className="error-actions">
              {isRecovering ? (
                <button className="btn-recovery" disabled>
                  🔄 Recovering...
                </button>
              ) : retryCount < 2 ? (
                <button className="btn-retry" onClick={this.handleRetry}>
                  🔄 Try Again
                </button>
              ) : null}
              
              <button className="btn-refresh" onClick={this.handleRefresh}>
                🔃 Refresh Page
              </button>
            </div>

            <div className="error-crisis-strip" style={{ marginTop: '1.5rem' }}>
              <PublicCrisisStrip />
            </div>
            
            {process.env.NODE_ENV === 'development' && error && (
              <details className="error-details">
                <summary>Technical Details</summary>
                <pre className="error-stack">
                  {error.toString()}
                  {this.state.errorInfo?.componentStack}
                </pre>
              </details>
            )}
          </div>
          
          <style jsx>{`
            .error-boundary {
              min-height: 50vh;
              display: flex;
              align-items: center;
              justify-content: center;
              padding: 2rem;
              background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);
            }
            
            .error-container {
              max-width: 500px;
              text-align: center;
              background: white;
              border-radius: 12px;
              padding: 3rem 2rem;
              box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
              border: 1px solid #e1e5e9;
            }
            
            .error-icon {
              font-size: 4rem;
              margin-bottom: 1rem;
            }
            
            /* v1.1: app errors use amber (warn), NOT crisis red. Crisis red is
               reserved for genuine crisis-support surfaces only. */
            .error-title {
              color: #9A5A12;
              margin-bottom: 1rem;
              font-size: 1.5rem;
              font-weight: 600;
            }
            
            .error-message {
              color: #2c3e50;
              margin-bottom: 0.5rem;
              font-size: 1.1rem;
              line-height: 1.5;
            }
            
            .error-action {
              color: #7f8c8d;
              margin-bottom: 2rem;
              font-size: 0.95rem;
            }
            
            .retry-info {
              color: #f39c12;
              font-size: 0.9rem;
              margin-bottom: 1.5rem;
              font-weight: 500;
            }
            
            .error-actions {
              display: flex;
              gap: 1rem;
              justify-content: center;
              flex-wrap: wrap;
            }
            
            .btn-retry,
            .btn-refresh,
            .btn-recovery {
              padding: 0.75rem 1.5rem;
              border: none;
              border-radius: 8px;
              font-size: 0.95rem;
              font-weight: 500;
              cursor: pointer;
              transition: all 0.3s ease;
              min-width: 120px;
            }
            
            .btn-retry {
              background: #5086ed;
              color: white;
            }

            .btn-retry:hover {
              background: #3A6ED4;
            }
            
            .btn-refresh {
              background: #95a5a6;
              color: white;
            }
            
            .btn-refresh:hover {
              background: #7f8c8d;
              transform: translateY(-2px);
            }
            
            .btn-recovery {
              background: #f39c12;
              color: white;
              cursor: not-allowed;
              opacity: 0.7;
            }
            
            .error-details {
              margin-top: 2rem;
              text-align: left;
              padding: 1rem;
              background: #f8f9fa;
              border-radius: 8px;
              border: 1px solid #e9ecef;
            }
            
            .error-details summary {
              cursor: pointer;
              font-weight: 500;
              color: #6c757d;
              margin-bottom: 1rem;
            }
            
            .error-stack {
              font-family: 'Courier New', monospace;
              font-size: 0.8rem;
              color: #495057;
              white-space: pre-wrap;
              word-break: break-word;
            }
            
            @media (max-width: 600px) {
              .error-container {
                margin: 1rem;
                padding: 2rem 1.5rem;
              }
              
              .error-actions {
                flex-direction: column;
                align-items: center;
              }
              
              .btn-retry,
              .btn-refresh,
              .btn-recovery {
                width: 100%;
                max-width: 200px;
              }
            }
          `}</style>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary; 